import { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { Link } from "react-router-dom";
import {
  Plus,
  Search,
  Building2,
  MapPin,
  Hash,
  Loader2,
  StickyNote,
  CircleCheck,
  X,
  ArrowLeft,
  CalendarClock,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

import { trpc } from "@/lib/trpc";
import { cn, daysSince } from "@/lib/utils";
import { useSearchShortcut } from "@/lib/use-search-shortcut";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { UserAvatar, userLabel } from "@/components/user-avatar";
type OpportunityStatus =
  | "opportunity"
  | "ready"
  | "approaching"
  | "quote_sent"
  | "negotiation"
  | "signing"
  | "won"
  | "lost";

const COLUMNS: { status: OpportunityStatus; label: string; tone: string }[] = [
  { status: "opportunity", label: "Opportunity", tone: "bg-slate-100 text-slate-700" },
  { status: "ready", label: "Ready", tone: "bg-zinc-100 text-zinc-700" },
  { status: "approaching", label: "Approaching", tone: "bg-sky-100 text-sky-700" },
  { status: "quote_sent", label: "Quote sent", tone: "bg-blue-100 text-blue-700" },
  { status: "negotiation", label: "Negotiation", tone: "bg-amber-100 text-amber-800" },
  { status: "signing", label: "Contract signing", tone: "bg-violet-100 text-violet-800" },
  { status: "won", label: "Won", tone: "bg-emerald-100 text-emerald-800" },
  { status: "lost", label: "Lost", tone: "bg-rose-100 text-rose-700" },
];

type OpportunityTag = {
  id: string;
  name: string;
  color: string | null;
};

type IndustryType = "accounting" | "business";

type AssignableUser = {
  id: string;
  name: string | null;
  email: string;
};

/** Sentinel used in the URL and in the filter set for opportunities without an owner. */
const UNASSIGNED_OWNER = "none";

const INDUSTRY_TYPE_BADGE: Record<IndustryType, { label: string; tone: string }> = {
  accounting: { label: "Accounting", tone: "bg-blue-100 text-blue-700 border-blue-300" },
  business: { label: "Business", tone: "bg-amber-100 text-amber-800 border-amber-300" },
};

type Opportunity = {
  id: string;
  name: string;
  status: OpportunityStatus;
  clientId: string | null;
  clientCompany: string | null;
  clientIndustryType: IndustryType | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  estimatedMrrEur: number | null;
  computedMrrEur: number | null;
  computedFixedMrrEur: number | null;
  computedUsageMrrEur: number | null;
  estimatedPlan: string | null;
  estimatedSeats: number | null;
  ownerId: string | null;
  ownerName?: string | null;
  ownerEmail?: string | null;
  updatedAt: Date | string;
  noteCount?: number;
  approvedAt?: Date | string | null;
  proposalLastViewedAt?: Date | string | null;
  nextActionAt?: Date | string | null;
  tags?: OpportunityTag[];
};

const NEXT_ACTION_FMT = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
});

function formatNextAction(date: Date | string | null | undefined): {
  label: string;
  tone: "overdue" | "today" | "upcoming";
} | null {
  if (!date) return null;
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return null;
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const today = startOfDay(new Date());
  const target = startOfDay(d);
  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    const overdue = -diffDays;
    return { label: overdue === 1 ? "1d overdue" : `${overdue}d overdue`, tone: "overdue" };
  }
  if (diffDays === 0) return { label: "Today", tone: "today" };
  if (diffDays === 1) return { label: "Tomorrow", tone: "upcoming" };
  return { label: NEXT_ACTION_FMT.format(d), tone: "upcoming" };
}

type DisplayMrr = {
  fixed: number;
  usage: number;
  total: number;
  fromProducts: boolean;
};

function getDisplayMrr(
  opp: Pick<
    Opportunity,
    "computedMrrEur" | "computedFixedMrrEur" | "computedUsageMrrEur" | "estimatedMrrEur"
  >
): DisplayMrr | null {
  if (opp.computedFixedMrrEur != null || opp.computedUsageMrrEur != null) {
    const fixed = opp.computedFixedMrrEur ?? 0;
    const usage = opp.computedUsageMrrEur ?? 0;
    return { fixed, usage, total: fixed + usage, fromProducts: true };
  }
  if (opp.computedMrrEur != null) {
    return {
      fixed: opp.computedMrrEur,
      usage: 0,
      total: opp.computedMrrEur,
      fromProducts: true,
    };
  }
  if (opp.estimatedMrrEur != null) {
    return {
      fixed: opp.estimatedMrrEur,
      usage: 0,
      total: opp.estimatedMrrEur,
      fromProducts: false,
    };
  }
  return null;
}

function formatEur(value: number): string {
  return `€${value.toLocaleString("en-US")}`;
}

function formatMrrParts(parts: { fixed: number; usage: number }): string {
  if (parts.usage > 0 && parts.fixed > 0) {
    return `${formatEur(parts.fixed)} + ${formatEur(parts.usage)}/mo`;
  }
  if (parts.usage > 0) {
    return `${formatEur(parts.usage)}/mo`;
  }
  if (parts.fixed > 0) {
    return `${formatEur(parts.fixed)}/mo`;
  }
  return "€0.00";
}

export function OpportunitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") ?? "";
  const tagParam = searchParams.getAll("tag").join(",");
  const selectedTagIds = useMemo(() => new Set(tagParam ? tagParam.split(",") : []), [tagParam]);
  const ownerParam = searchParams.getAll("owner").join(",");
  const selectedOwnerIds = useMemo(
    () => new Set(ownerParam ? ownerParam.split(",") : []),
    [ownerParam]
  );
  const showOverdueOnly = searchParams.get("overdue") === "1";

  const setSearch = (value: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("search", value);
        else next.delete("search");
        return next;
      },
      { replace: true }
    );

  const setSelectedTagIds = (updater: (prev: Set<string>) => Set<string>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tag");
        const newSet = updater(new Set(prev.getAll("tag")));
        for (const id of newSet) next.append("tag", id);
        return next;
      },
      { replace: true }
    );
  };

  const setSelectedOwnerIds = (updater: (prev: Set<string>) => Set<string>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("owner");
        const newSet = updater(new Set(prev.getAll("owner")));
        for (const id of newSet) next.append("owner", id);
        return next;
      },
      { replace: true }
    );
  };

  const setShowOverdueOnly = (updater: (prev: boolean) => boolean) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const newVal = updater(prev.get("overdue") === "1");
        if (newVal) next.set("overdue", "1");
        else next.delete("overdue");
        return next;
      },
      { replace: true }
    );

  const [createOpen, setCreateOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const navigate = useNavigate();
  const searchInputRef = useSearchShortcut();

  const utils = trpc.useUtils();
  const list = trpc.opportunities.list.useQuery({ search: search || undefined });

  const updateStatus = trpc.opportunities.updateStatus.useMutation({
    onMutate: async ({ id, status }) => {
      await utils.opportunities.list.cancel();
      const prev = utils.opportunities.list.getData({ search: search || undefined });
      if (prev) {
        utils.opportunities.list.setData(
          { search: search || undefined },
          prev.map((o) => (o.id === id ? { ...o, status, updatedAt: new Date() } : o))
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utils.opportunities.list.setData({ search: search || undefined }, ctx.prev);
      toast.error("Failed to update status");
    },
    onSettled: () => utils.opportunities.list.invalidate(),
  });

const create = trpc.opportunities.create.useMutation({
    onSuccess: (data) => {
      utils.opportunities.list.invalidate();
      setCreateOpen(false);
      toast.success("Opportunity created");
      navigate(`/o/${data.id}`);
    },
    onError: (err) => {
      toast.error(`Failed to create opportunity: ${err.message}`);
    },
  });

  const allTagsQuery = trpc.opportunityTags.list.useQuery();
  const allTags = allTagsQuery.data ?? [];

  const usersQuery = trpc.users.listAssignable.useQuery();
  const allUsers = (usersQuery.data ?? []) as AssignableUser[];

  const setOwner = trpc.opportunities.update.useMutation({
    onMutate: async ({ id, patch }) => {
      await utils.opportunities.list.cancel();
      const prev = utils.opportunities.list.getData({ search: search || undefined });
      const owner = allUsers.find((u) => u.id === patch.ownerId) ?? null;
      if (prev) {
        utils.opportunities.list.setData(
          { search: search || undefined },
          prev.map((o) =>
            o.id === id
              ? {
                  ...o,
                  ownerId: owner?.id ?? null,
                  ownerName: owner?.name ?? null,
                  ownerEmail: owner?.email ?? null,
                }
              : o
          )
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utils.opportunities.list.setData({ search: search || undefined }, ctx.prev);
      toast.error("Failed to change owner");
    },
    onSettled: () => utils.opportunities.list.invalidate(),
  });

  const setTags = trpc.opportunityTags.setForOpportunity.useMutation({
    onMutate: async ({ opportunityId, tagIds }) => {
      await utils.opportunities.list.cancel();
      const prev = utils.opportunities.list.getData({ search: search || undefined });
      const tagById = new Map(allTags.map((t) => [t.id, t]));
      const nextTags = tagIds
        .map((id) => tagById.get(id))
        .filter((t): t is (typeof allTags)[number] => !!t)
        .map((t) => ({ id: t.id, name: t.name, color: t.color }));
      if (prev) {
        utils.opportunities.list.setData(
          { search: search || undefined },
          prev.map((o) => (o.id === opportunityId ? { ...o, tags: nextTags } : o))
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utils.opportunities.list.setData({ search: search || undefined }, ctx.prev);
      toast.error("Failed to update tags");
    },
    onSettled: () => utils.opportunities.list.invalidate(),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const id = String(e.active.id);
    const overId = e.over?.id;
    if (!overId) return;
    const targetStatus = String(overId).replace("col:", "") as OpportunityStatus;
    const opp = (list.data ?? []).find((o) => o.id === id);
    if (!opp || opp.status === targetStatus) return;
    updateStatus.mutate({ id, status: targetStatus });
  };

  const allOpportunities = (list.data as Opportunity[] | undefined) ?? [];
  const tagCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const o of allOpportunities) {
      for (const t of o.tags ?? []) {
        counts.set(t.id, (counts.get(t.id) ?? 0) + 1);
      }
    }
    return counts;
  }, [allOpportunities]);
  const ownerCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const o of allOpportunities) {
      const key = o.ownerId ?? UNASSIGNED_OWNER;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [allOpportunities]);
  const opportunities = useMemo(() => {
    let filtered = allOpportunities;
    if (selectedTagIds.size > 0) {
      filtered = filtered.filter((o) => (o.tags ?? []).some((t) => selectedTagIds.has(t.id)));
    }
    if (selectedOwnerIds.size > 0) {
      filtered = filtered.filter((o) => selectedOwnerIds.has(o.ownerId ?? UNASSIGNED_OWNER));
    }
    if (showOverdueOnly) {
      filtered = filtered.filter((o) => formatNextAction(o.nextActionAt)?.tone === "overdue");
    }
    return filtered;
  }, [allOpportunities, selectedTagIds, selectedOwnerIds, showOverdueOnly]);

  const toggleOwner = (id: string) => {
    setSelectedOwnerIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTag = (id: string) => {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const grouped = COLUMNS.map((c) => {
    const items = opportunities.filter((o) => o.status === c.status);
    items.sort((a, b) => {
      const aTop = (a.tags ?? []).some((t) => t.name.toUpperCase() === "TOP") ? 0 : 1;
      const bTop = (b.tags ?? []).some((t) => t.name.toUpperCase() === "TOP") ? 0 : 1;
      return aTop - bTop;
    });
    return { ...c, items };
  });

  const activeOpp = opportunities.find((o) => o.id === activeId);

  return (
    <div className="w-full px-4 py-6 md:px-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Opportunities</h1>
          <p className="text-sm text-muted-foreground">
            {opportunities.length} active opportunities
          </p>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              placeholder="Search by name, email, product, or accounting system..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-8 sm:w-56"
            />
            {search && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setSearch("");
                  searchInputRef.current?.focus();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {allTags.length > 0 && (
            <div className="w-44 sm:w-56">
              <TagFilterMultiSelect
                allTags={allTags}
                tagCounts={tagCounts}
                selectedTagIds={selectedTagIds}
                onToggleTag={toggleTag}
                onClear={() => setSelectedTagIds(() => new Set())}
                totalCount={allOpportunities.length}
              />
            </div>
          )}
          {allUsers.length > 0 && (
            <OwnerFilterMultiSelect
              allUsers={allUsers}
              ownerCounts={ownerCounts}
              selectedOwnerIds={selectedOwnerIds}
              onToggleOwner={toggleOwner}
              onClear={() => setSelectedOwnerIds(() => new Set())}
              totalCount={allOpportunities.length}
            />
          )}
          <Button
            variant={showOverdueOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setShowOverdueOnly((v) => !v)}
            className="shrink-0"
          >
            <CalendarClock className="mr-1.5 h-4 w-4" />
            Overdue
          </Button>
          <CreateOpportunityDialog
            open={createOpen}
            onOpenChange={setCreateOpen}
            onCreate={(data) => create.mutate(data)}
            pending={create.isPending}
            allTags={allTags}
          />
        </div>
      </div>

      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="-mx-4 overflow-x-auto px-4 pb-4 md:-mx-6 md:px-6 lg:max-h-[calc(100vh-12rem)] lg:overflow-auto">
          <div className="flex w-full min-w-fit gap-4">
            {grouped.map((col) => {
              const stageMrr = col.items.reduce(
                (acc, o) => {
                  const m = getDisplayMrr(o);
                  return {
                    fixed: acc.fixed + (m?.fixed ?? 0),
                    usage: acc.usage + (m?.usage ?? 0),
                  };
                },
                { fixed: 0, usage: 0 }
              );
              return (
              <KanbanColumn
                key={col.status}
                status={col.status}
                label={col.label}
                tone={col.tone}
                count={col.items.length}
                stageMrr={stageMrr}
                loading={list.isLoading}
              >
                {col.items.map((o) => (
                  <DraggableCard
                    key={o.id}
                    opp={o}
                    allTags={allTags}
                    onSetTags={(tagIds) =>
                      setTags.mutate({ opportunityId: o.id, tagIds })
                    }
                    allUsers={allUsers}
                    onSetOwner={(ownerId) =>
                      setOwner.mutate({ id: o.id, patch: { ownerId } })
                    }
                  />
                ))}
                {!list.isLoading && col.items.length === 0 && (
                  <div className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">
                    Empty
                  </div>
                )}
              </KanbanColumn>
              );
            })}
          </div>
        </div>

        <DragOverlay dropAnimation={null}>
          {activeOpp ? <OpportunityCard opp={activeOpp} dragging /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

function KanbanColumn({
  status,
  label,
  tone,
  count,
  stageMrr,
  children,
  loading,
}: {
  status: OpportunityStatus;
  label: string;
  tone: string;
  count: number;
  stageMrr: { fixed: number; usage: number };
  loading: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${status}` });
  return (
    <div className="flex w-72 shrink-0 flex-col lg:w-auto lg:min-w-[220px] lg:flex-1 lg:shrink">
      <div className="sticky top-0 z-10 mb-2 flex flex-col gap-1 bg-background px-1 py-2">
        <div className="flex items-center gap-2">
          <span className={cn("rounded-md px-2 py-0.5 text-xs font-semibold", tone)}>
            {label}
          </span>
          <span className="text-xs font-medium text-muted-foreground">{count}</span>
        </div>
        <span className="text-xs font-semibold tabular-nums text-muted-foreground">
          {formatMrrParts(stageMrr)}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          "flex min-h-[200px] flex-1 flex-col gap-2 rounded-lg bg-muted/40 p-2 transition-colors",
          isOver && "bg-accent ring-2 ring-ring"
        )}
      >
        {loading ? (
          <>
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function DraggableCard({
  opp,
  allTags,
  onSetTags,
  allUsers,
  onSetOwner,
}: {
  opp: Opportunity;
  allTags: OpportunityTag[];
  onSetTags: (tagIds: string[]) => void;
  allUsers: AssignableUser[];
  onSetOwner: (ownerId: string | null) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: opp.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(isDragging && "opacity-30")}
    >
      <OpportunityCard
        opp={opp}
        allTags={allTags}
        onSetTags={onSetTags}
        allUsers={allUsers}
        onSetOwner={onSetOwner}
      />
    </div>
  );
}

function OpportunityCard({
  opp,
  dragging,
  allTags,
  onSetTags,
  allUsers,
  onSetOwner,
}: {
  opp: Opportunity;
  dragging?: boolean;
  allTags?: OpportunityTag[];
  onSetTags?: (tagIds: string[]) => void;
  allUsers?: AssignableUser[];
  onSetOwner?: (ownerId: string | null) => void;
}) {
  const days = daysSince(opp.updatedAt);
  const mrr = getDisplayMrr(opp);
  const isApproved = opp.approvedAt != null;
  const wasOpened = opp.proposalLastViewedAt != null;
  const tags = opp.tags ?? [];
  const isTop = tags.some((t) => t.name.toUpperCase() === "TOP");
  const canManageTags = !!allTags && !!onSetTags && !dragging;
  const canChangeOwner = !!allUsers && !!onSetOwner && !dragging;
  const nextAction = formatNextAction(opp.nextActionAt);
  return (
    <div className="group relative">
      <div className="absolute right-2 top-2 z-10">
        {canChangeOwner ? (
          <OwnerPicker
            allUsers={allUsers!}
            ownerId={opp.ownerId}
            ownerName={opp.ownerName}
            ownerEmail={opp.ownerEmail}
            onSetOwner={onSetOwner!}
          />
        ) : (
          <UserAvatar id={opp.ownerId} name={opp.ownerName} email={opp.ownerEmail} />
        )}
      </div>
      <Link to={`/o/${opp.id}`} onClick={(e) => dragging && e.preventDefault()}>
        <Card
          className={cn(
            "cursor-grab rounded-lg p-3 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing",
            dragging && "rotate-1 shadow-lg ring-2 ring-ring",
            isTop && "ring-2 ring-amber-400 bg-amber-50/60 dark:bg-amber-950/30"
          )}
        >
          <div className="mb-1 flex items-start gap-1.5 pr-8">
            {(isApproved || wasOpened) && (
              <CircleCheck
                className={cn(
                  "mt-0.5 h-4 w-4 shrink-0",
                  isApproved ? "text-emerald-600" : "text-zinc-400"
                )}
                aria-label={
                  isApproved
                    ? "Approved"
                    : "Landing page opened by client"
                }
              />
            )}
            <div className="line-clamp-2 text-sm font-semibold leading-tight">{opp.name}</div>
          </div>
          {opp.clientCompany && (
            <div className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Building2 className="h-3 w-3 shrink-0" />
              <span className="line-clamp-1 min-w-0 flex-1">{opp.clientCompany}</span>
              {opp.clientIndustryType && (
                <span
                  className={cn(
                    "shrink-0 rounded-full border px-1.5 py-0 text-[10px] font-medium leading-tight",
                    INDUSTRY_TYPE_BADGE[opp.clientIndustryType].tone
                  )}
                >
                  {INDUSTRY_TYPE_BADGE[opp.clientIndustryType].label}
                </span>
              )}
            </div>
          )}
          {!opp.clientCompany && opp.clientIndustryType && (
            <div className="mb-2">
              <span
                className={cn(
                  "inline-block rounded-full border px-1.5 py-0 text-[10px] font-medium leading-tight",
                  INDUSTRY_TYPE_BADGE[opp.clientIndustryType].tone
                )}
              >
                {INDUSTRY_TYPE_BADGE[opp.clientIndustryType].label}
              </span>
            </div>
          )}
          {(tags.length > 0 || canManageTags) && (
            <div className="mb-2 flex flex-wrap items-center gap-1">
              {tags.map((t) => (
                <TagBadge
                  key={t.id}
                  tag={t}
                  onRemove={
                    canManageTags
                      ? () => onSetTags!(tags.filter((x) => x.id !== t.id).map((x) => x.id))
                      : undefined
                  }
                />
              ))}
              {canManageTags && (
                <AddTagButton
                  allTags={allTags!}
                  assigned={tags}
                  onSetTags={onSetTags!}
                />
              )}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 text-xs">
            <div className="text-muted-foreground">
              {opp.estimatedPlan && <span className="font-medium">{opp.estimatedPlan}</span>}
              {opp.estimatedPlan && opp.estimatedSeats ? " · " : ""}
              {opp.estimatedSeats && <span>{opp.estimatedSeats} seats</span>}
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            {days != null ? (
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {days === 0 ? "today" : `${days}d ago`}
              </div>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-1.5">
              {nextAction && (
                <div
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                    nextAction.tone === "overdue" &&
                      "bg-rose-100 text-rose-700",
                    nextAction.tone === "today" &&
                      "bg-amber-100 text-amber-800",
                    nextAction.tone === "upcoming" &&
                      "bg-sky-100 text-sky-700"
                  )}
                  title="Next action"
                >
                  <CalendarClock className="h-3 w-3" />
                  {nextAction.label}
                </div>
              )}
              {opp.noteCount != null && opp.noteCount > 0 && (
                <div className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  <StickyNote className="h-3 w-3" />
                  {opp.noteCount}
                </div>
              )}
            </div>
          </div>
          {opp.status !== "lost" && mrr != null && mrr.total > 0 && (
            <div
              className="-mx-3 -mb-3 mt-2 rounded-b-lg bg-zinc-200 px-3 py-1.5 text-center text-xs font-semibold tabular-nums text-zinc-700"
              title={
                mrr.fromProducts
                  ? mrr.usage > 0
                    ? "Fixed MRR + potential usage MRR from selected products"
                    : "MRR from selected products"
                  : "Estimated MRR"
              }
            >
              {formatMrrParts(mrr)}
              {!mrr.fromProducts && (
                <span className="ml-1 font-normal text-zinc-500">(est.)</span>
              )}
            </div>
          )}
        </Card>
      </Link>
    </div>
  );
}

function stopCardDrag(e: React.SyntheticEvent) {
  e.preventDefault();
  e.stopPropagation();
}

/** Owner circle on a card; clicking it reassigns the opportunity. */
function OwnerPicker({
  allUsers,
  ownerId,
  ownerName,
  ownerEmail,
  onSetOwner,
}: {
  allUsers: AssignableUser[];
  ownerId: string | null;
  ownerName?: string | null;
  ownerEmail?: string | null;
  onSetOwner: (ownerId: string | null) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Change owner"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={stopCardDrag}
          className="rounded-full transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <UserAvatar id={ownerId} name={ownerName} email={ownerEmail} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56 p-0"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuLabel>Owner</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-64 overflow-y-auto">
          {allUsers.map((u) => (
            <DropdownMenuCheckboxItem
              key={u.id}
              checked={ownerId === u.id}
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={() => onSetOwner(ownerId === u.id ? null : u.id)}
            >
              <UserAvatar id={u.id} name={u.name} email={u.email} size="xs" className="mr-2" />
              <span className="flex-1 truncate">{userLabel(u.name, u.email)}</span>
            </DropdownMenuCheckboxItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem
            checked={!ownerId}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => onSetOwner(null)}
          >
            <span className="flex-1 text-muted-foreground">Unassigned</span>
          </DropdownMenuCheckboxItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function OwnerFilterMultiSelect({
  allUsers,
  ownerCounts,
  selectedOwnerIds,
  onToggleOwner,
  onClear,
  totalCount,
}: {
  allUsers: AssignableUser[];
  ownerCounts: Map<string, number>;
  selectedOwnerIds: Set<string>;
  onToggleOwner: (id: string) => void;
  onClear: () => void;
  totalCount: number;
}) {
  const selectedUsers = allUsers.filter((u) => selectedOwnerIds.has(u.id));
  const unassignedSelected = selectedOwnerIds.has(UNASSIGNED_OWNER);
  const selectedCount = [...selectedOwnerIds].reduce(
    (acc, id) => acc + (ownerCounts.get(id) ?? 0),
    0
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium shadow-sm hover:bg-muted"
        >
          {selectedOwnerIds.size === 0 ? (
            <span className="text-muted-foreground">All owners</span>
          ) : (
            <span className="flex items-center -space-x-1.5">
              {unassignedSelected && <UserAvatar id={null} size="xs" />}
              {selectedUsers.slice(0, 3).map((u) => (
                <UserAvatar key={u.id} id={u.id} name={u.name} email={u.email} size="xs" />
              ))}
              {selectedUsers.length > 3 && (
                <span className="pl-2.5 text-xs text-muted-foreground">
                  +{selectedUsers.length - 3}
                </span>
              )}
            </span>
          )}
          <span className="rounded bg-muted px-1.5 text-[11px] font-normal text-muted-foreground">
            {selectedOwnerIds.size === 0 ? totalCount : selectedCount}
          </span>
          {selectedOwnerIds.size > 0 && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear owner filter"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClear();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                }
              }}
              className="shrink-0 rounded-sm text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" />
            </span>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60 p-0">
        <DropdownMenuLabel>Filter by owner</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={selectedOwnerIds.size === 0}
          onSelect={(e) => e.preventDefault()}
          onCheckedChange={() => onClear()}
        >
          <span className="flex-1">All</span>
          <span className="ml-2 rounded bg-muted px-1.5 text-[11px] font-normal text-muted-foreground">
            {totalCount}
          </span>
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        <div className="max-h-64 overflow-y-auto">
          {allUsers.map((u) => (
            <DropdownMenuCheckboxItem
              key={u.id}
              checked={selectedOwnerIds.has(u.id)}
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={() => onToggleOwner(u.id)}
            >
              <UserAvatar id={u.id} name={u.name} email={u.email} size="xs" className="mr-2" />
              <span className="flex-1 truncate">{userLabel(u.name, u.email)}</span>
              <span className="ml-2 rounded bg-muted px-1.5 text-[11px] font-normal text-muted-foreground">
                {ownerCounts.get(u.id) ?? 0}
              </span>
            </DropdownMenuCheckboxItem>
          ))}
          <DropdownMenuCheckboxItem
            checked={unassignedSelected}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => onToggleOwner(UNASSIGNED_OWNER)}
          >
            <UserAvatar id={null} size="xs" className="mr-2" />
            <span className="flex-1 text-muted-foreground">Unassigned</span>
            <span className="ml-2 rounded bg-muted px-1.5 text-[11px] font-normal text-muted-foreground">
              {ownerCounts.get(UNASSIGNED_OWNER) ?? 0}
            </span>
          </DropdownMenuCheckboxItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TagBadge({
  tag,
  onRemove,
}: {
  tag: OpportunityTag;
  onRemove?: () => void;
}) {
  return (
    <Badge
      variant="secondary"
      className="gap-0.5 pl-1.5 pr-0.5 text-[10px] font-normal leading-none"
      style={
        tag.color
          ? { backgroundColor: `${tag.color}1f`, borderColor: tag.color, color: tag.color }
          : undefined
      }
    >
      <span>{tag.name}</span>
      {onRemove && (
        <button
          type="button"
          aria-label={`Remove ${tag.name}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            stopCardDrag(e);
            onRemove();
          }}
          className="rounded-full p-0.5 text-current opacity-70 hover:bg-foreground/10 hover:opacity-100"
        >
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </Badge>
  );
}

function TagFilterMultiSelect({
  allTags,
  tagCounts,
  selectedTagIds,
  onToggleTag,
  onClear,
  totalCount,
}: {
  allTags: OpportunityTag[];
  tagCounts: Map<string, number>;
  selectedTagIds: Set<string>;
  onToggleTag: (id: string) => void;
  onClear: () => void;
  totalCount: number;
}) {
  const selectedTags = allTags.filter((t) => selectedTagIds.has(t.id));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium shadow-sm hover:bg-muted"
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {selectedTags.length === 0 ? (
              <span className="text-muted-foreground">All tags</span>
            ) : (
              selectedTags.map((t) => (
                <span
                  key={t.id}
                  className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] font-medium leading-none"
                  style={
                    t.color
                      ? {
                          backgroundColor: `${t.color}1f`,
                          borderColor: t.color,
                          color: t.color,
                        }
                      : undefined
                  }
                >
                  <span
                    className="inline-block h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: t.color ?? "transparent" }}
                    aria-hidden
                  />
                  {t.name}
                </span>
              ))
            )}
            <span className="rounded bg-muted px-1.5 text-[11px] font-normal text-muted-foreground">
              {selectedTags.length === 0
                ? totalCount
                : selectedTags.reduce((acc, t) => acc + (tagCounts.get(t.id) ?? 0), 0)}
            </span>
          </span>
          {selectedTags.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear tag filter"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClear();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                }
              }}
              className="shrink-0 rounded-sm text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" />
            </span>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width] p-0">
        <DropdownMenuLabel>Filter by tag</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={selectedTagIds.size === 0}
          onSelect={(e) => e.preventDefault()}
          onCheckedChange={() => onClear()}
        >
          <span className="flex-1">All</span>
          <span className="ml-2 rounded bg-muted px-1.5 text-[11px] font-normal text-muted-foreground">
            {totalCount}
          </span>
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        <div className="max-h-64 overflow-y-auto">
          {allTags.map((t) => {
            const count = tagCounts.get(t.id) ?? 0;
            return (
              <DropdownMenuCheckboxItem
                key={t.id}
                checked={selectedTagIds.has(t.id)}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() => onToggleTag(t.id)}
              >
                <span
                  className="mr-2 inline-block h-2.5 w-2.5 shrink-0 rounded-full border"
                  style={{ backgroundColor: t.color ?? "transparent" }}
                  aria-hidden
                />
                <span className="flex-1">{t.name}</span>
                <span className="ml-2 rounded bg-muted px-1.5 text-[11px] font-normal text-muted-foreground">
                  {count}
                </span>
              </DropdownMenuCheckboxItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AddTagButton({
  allTags,
  assigned,
  onSetTags,
}: {
  allTags: OpportunityTag[];
  assigned: OpportunityTag[];
  onSetTags: (tagIds: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const assignedIds = useMemo(() => new Set(assigned.map((t) => t.id)), [assigned]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allTags;
    return allTags.filter((t) => t.name.toLowerCase().includes(q));
  }, [allTags, search]);

  const toggle = (id: string, checked: boolean) => {
    const next = new Set(assignedIds);
    if (checked) next.add(id);
    else next.delete(id);
    onSetTags([...next]);
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Add tag"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={stopCardDrag}
          className="inline-flex h-5 items-center gap-0.5 rounded-full border border-dashed border-muted-foreground/40 px-1.5 text-[10px] text-muted-foreground hover:border-foreground hover:text-foreground"
        >
          <Plus className="h-2.5 w-2.5" />
          {assigned.length === 0 && <span>Tag</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-56 p-0"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuLabel>Tags</DropdownMenuLabel>
        <div className="px-2 pb-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder="Search…"
              className="h-7 pl-7 text-xs"
            />
          </div>
        </div>
        <DropdownMenuSeparator />
        <div className="max-h-64 overflow-y-auto">
          {allTags.length === 0 ? (
            <div className="px-2 py-3 text-xs text-muted-foreground">
              No tags. Create them in Settings → Opportunity tags.
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-2 py-3 text-xs text-muted-foreground">Nothing found.</div>
          ) : (
            filtered.map((t) => (
              <DropdownMenuCheckboxItem
                key={t.id}
                checked={assignedIds.has(t.id)}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={(checked) => toggle(t.id, !!checked)}
              >
                <span
                  className="mr-2 inline-block h-2.5 w-2.5 shrink-0 rounded-full border"
                  style={{ backgroundColor: t.color ?? "transparent" }}
                  aria-hidden
                />
                <span className="flex-1">{t.name}</span>
              </DropdownMenuCheckboxItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type CreatePayload = {
  name: string;
  clientCompany?: string;
  clientRegCode?: string;
  clientCity?: string;
  meilisearchOrgId?: string;
  contactEmail?: string;
  contactPhone?: string;
  language?: "lt" | "en";
  industryType?: IndustryType;
  tagIds?: string[];
};

type CompanyHit = {
  id?: string;
  name: string;
  fullName?: string | null;
  regCode?: string | null;
  city?: string | null;
  orgType?: string | null;
  email?: string | null;
  phone?: string | null;
};

function CreateOpportunityDialog({
  open,
  onOpenChange,
  onCreate,
  pending,
  allTags,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreate: (data: CreatePayload) => void;
  pending: boolean;
  allTags: OpportunityTag[];
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<CompanyHit | null>(null);
  const [industryType, setIndustryType] = useState<IndustryType>("accounting");
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());

  const toggleTag = (id: string) =>
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebouncedQuery("");
      setSelectedCompany(null);
      setIndustryType("accounting");
      setSelectedTagIds(new Set());
    }
  }, [open]);

  const search = trpc.companies.search.useQuery(
    { query: debouncedQuery },
    { enabled: debouncedQuery.trim().length > 0 }
  );

  const hits = search.data ?? [];
  const isSearching = search.isFetching;
  const hasError = search.isError;
  const hasQuery = debouncedQuery.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" />
          New
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {selectedCompany ? "Confirm new opportunity" : "New opportunity"}
          </DialogTitle>
          <DialogDescription>
            {selectedCompany
              ? "Review the selected company and pick its industry type."
              : "Search for a company by name and pick it from the list."}
          </DialogDescription>
        </DialogHeader>

        {selectedCompany ? (
          <div className="space-y-4">
            <div className="rounded-md border bg-muted/30 p-3">
              <div className="flex items-start gap-2">
                <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium leading-tight">
                    {selectedCompany.fullName || selectedCompany.name}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    {selectedCompany.city && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {selectedCompany.city}
                      </span>
                    )}
                    {selectedCompany.orgType && <span>{selectedCompany.orgType}</span>}
                    {selectedCompany.regCode && (
                      <span className="flex items-center gap-1">
                        <Hash className="h-3 w-3" />
                        {selectedCompany.regCode}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="industry-type">Company type</Label>
              <Select
                value={industryType}
                onValueChange={(v) => setIndustryType(v as IndustryType)}
                disabled={pending}
              >
                <SelectTrigger id="industry-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="accounting">Accounting</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {allTags.length > 0 && (
              <div className="space-y-1.5">
                <Label>Tags</Label>
                <div className="flex flex-wrap gap-1.5">
                  {allTags.map((tag) => {
                    const active = selectedTagIds.has(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        disabled={pending}
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                          active
                            ? "border-foreground/30 bg-foreground/10 text-foreground"
                            : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
                        )}
                      >
                        {tag.color && (
                          <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: tag.color }}
                          />
                        )}
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setSelectedCompany(null)}
                disabled={pending}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                type="button"
                onClick={() => {
                  const hit = selectedCompany;
                  onCreate({
                    name: hit.fullName || hit.name,
                    clientCompany: hit.fullName || hit.name,
                    clientRegCode: hit.regCode || undefined,
                    clientCity: hit.city || undefined,
                    meilisearchOrgId: hit.id || undefined,
                    contactEmail: hit.email || undefined,
                    contactPhone: hit.phone || undefined,
                    language: "lt",
                    industryType,
                    tagIds: [...selectedTagIds],
                  });
                }}
                disabled={pending}
              >
                {pending && <Loader2 className="h-4 w-4 animate-spin" />}
                Create opportunity
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                placeholder="Company name..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
              )}
            </div>

            {hasQuery && (
              <ScrollArea className="h-64 rounded-md border">
                {hasError ? (
                  <div className="flex h-full flex-col items-center justify-center gap-1 p-6 text-center text-sm text-destructive">
                    <Building2 className="h-8 w-8 opacity-30" />
                    <p>Search failed. Check your connection and try again.</p>
                  </div>
                ) : !isSearching && hits.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center gap-1 p-6 text-center text-sm text-muted-foreground">
                    <Building2 className="h-8 w-8 opacity-30" />
                    <p>No companies found for "{debouncedQuery}"</p>
                  </div>
                ) : (
                  <ul className="divide-y">
                    {hits.map((hit) => (
                      <li key={hit.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedCompany(hit)}
                          className="w-full px-4 py-3 text-left transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
                        >
                          <div className="mb-0.5 text-sm font-medium leading-tight">
                            {hit.fullName || hit.name}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                            {hit.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {hit.city}
                              </span>
                            )}
                            {hit.orgType && <span>{hit.orgType}</span>}
                            {hit.regCode && (
                              <span className="flex items-center gap-1">
                                <Hash className="h-3 w-3" />
                                {hit.regCode}
                              </span>
                            )}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </ScrollArea>
            )}

            {!hasQuery && (
              <p className="text-center text-xs text-muted-foreground py-2">
                Start typing a company name
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
